import {
  Opportunity,
  OrganizationManager,
  type Organization,
  type PartnerOrganization,
} from "@entities";
import { organizationManagerRepo, partnerOrganizationRepo } from "@repositories";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { createPhoneNumberFilter, type HyperTableFilter } from "@shared/frontend/hyper-table";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import {
  buildPhoneNumberFieldFilter,
  getPhoneFilterInputValue,
  getPhoneSearchMode,
  normalizePhoneDigitsForSearch,
  type PhoneSearchMode,
} from "@shared/helpers/normalize-phone-for-search";
import { PhoneNumberFilterInput } from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/filter-types/PhoneNumberFilterInput";
import { VolunteerRegistrationOutcome } from "@shared/helpers/volunteer-registration-outcome";
import {
  OpportunityVolunteerMode,
  OPPORTUNITY_VOLUNTEER_MODE_HEB,
  type OpportunityVolunteerModeType,
} from "@shared/helpers/opportunity-volunteer-mode";
import {
  OPPORTUNITY_ADMIN_REGION_COASTAL_CODES,
  OPPORTUNITY_ADMIN_REGION_JERUSALEM_CODES,
} from "@shared/helpers/israel-cbs-regions";
import {
  opportunitiesWithPartnerOrganizationWhere,
  opportunitiesWithoutPartnerOrganizationFilter,
} from "@shared/helpers/partner-org-opportunity-status";
import CustomBooleanFilter from "@shared/frontend/hyper-table/inner-components/hyper-table-toolbar/filter-types/CustomBooleanFilter";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { FormGoogle } from "../../../components/form-components/form-google-autocomplete/FormGoogle";
import { BasicButton } from "../../../components/generic-components/button/Button";
import { TeamsSelect } from "../../../components/teams-select";
import { opportunityAgeRangeFilter } from "../opportunity-age-range-filter";
import { opportunityAmountRangeFilter } from "../opportunity-amount-range-filter";

const URGENCY_OPTIONS = [
  { value: "STANDARD", label: "רגילה" },
  { value: "HIGH", label: "גבוהה" },
  { value: "CRITICAL", label: "קריטית" },
] as const;
type ManagerUrgencyValue = (typeof URGENCY_OPTIONS)[number]["value"];

const VOLUNTEER_REGISTRATION_FLOW_OPTIONS = [
  { value: VolunteerRegistrationOutcome.REGULAR, label: "הרשמה רגילה" },
  { value: VolunteerRegistrationOutcome.INTERESTED, label: "התעניינות (ממתין לאישור)" },
  { value: VolunteerRegistrationOutcome.CHAT, label: "הרשמה + צ'אט" },
  { value: VolunteerRegistrationOutcome.SMS_DETAILS, label: "הרשמה + פרטים ב-SMS" },
] as const;

type VolunteerRegistrationFlowValue =
  (typeof VOLUNTEER_REGISTRATION_FLOW_OPTIONS)[number]["value"];

const VOLUNTEER_MODE_OPTIONS = (
  Object.values(OpportunityVolunteerMode) as OpportunityVolunteerModeType[]
).map((value) => ({
  value,
  label: OPPORTUNITY_VOLUNTEER_MODE_HEB[value],
}));

const toVolunteerRegistrationFlows = (
  value: unknown,
): VolunteerRegistrationFlowValue[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is VolunteerRegistrationFlowValue =>
    VOLUNTEER_REGISTRATION_FLOW_OPTIONS.some((opt) => opt.value === item),
  );
};

const toVolunteerModes = (value: unknown): OpportunityVolunteerModeType[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is OpportunityVolunteerModeType =>
    VOLUNTEER_MODE_OPTIONS.some((opt) => opt.value === item),
  );
};

const toUrgencies = (value: unknown): ManagerUrgencyValue[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is ManagerUrgencyValue =>
    URGENCY_OPTIONS.some((option) => option.value === item),
  );
};

const CALL_CENTER_URGENCY_OPTIONS = Array.from({ length: 11 }, (_, value) => ({
  value,
  label: value === 0 ? "0 - ללא" : String(value),
}));

const CALL_CENTER_DIFFICULTY_OPTIONS = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label:
    value <= 2
      ? `${value} - קלה`
      : value === 3
        ? "3 - בינונית"
        : `${value} - קשה`,
}));

function toSelectedNumbers(value: unknown, allowed: readonly number[]): number[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => Number(item))
    .filter((item) => allowed.includes(item));
}

function callCenterLevelFilter(
  id: string,
  caption: string,
  field: "callCenterUrgency" | "callCenterDifficulty",
  options: { value: number; label: string }[],
): HyperTableFilter<Opportunity> {
  const allowed = options.map((option) => option.value);
  return {
    id,
    icon: "literal",
    caption,
    renderFilter: ({ onSave, meta }) => {
      const [selected, setSelected] = useState<number[]>(
        toSelectedNumbers(meta.selected, allowed),
      );

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75rem",
            paddingInline: "0.5rem",
          }}
        >
          <Select
            multiple
            value={selected}
            onChange={(event) => {
              const { value } = event.target;
              const next = typeof value === "string" ? value.split(",") : value;
              setSelected(toSelectedNumbers(next, allowed));
            }}
            renderValue={(current) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {current.map((value) => (
                  <Chip
                    key={value}
                    label={options.find((option) => option.value === value)?.label ?? value}
                    size="small"
                  />
                ))}
              </Box>
            )}
            MenuProps={{
              disableScrollLock: true,
              PaperProps: { sx: { borderRadius: "20px" } },
            }}
            fullWidth
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selected.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                onSave({
                  value: selected.length
                    ? Opportunity.hyperFilter({
                      [field]: { $in: selected },
                    })
                    : {},
                  meta: { selected },
                });
              }}
            >
              שמירה
            </Button>
          </div>
        </div>
      );
    },
  };
}

const toValidDate = (v: unknown): Date | null => {
  if (v == null) return null;
  const d = v instanceof Date ? v : new Date(v as string | number);
  return Number.isFinite(d.getTime()) ? d : null;
};

const makeOpportunityDateRangeFilter = (
  id: string,
  caption: string,
  field: "createdAt" | "updatedAt",
): HyperTableFilter<Opportunity> => ({
  id,
  icon: "date",
  caption,
  renderFilter: ({ onSave, meta }) => {
    const raw = (meta as any) ?? {};
    const startDate = toValidDate(raw.startDate);
    const endDate = toValidDate(raw.endDate);

    const buildFilter = (start: Date | null, end: Date | null) => {
      if (!start && !end) return {};
      return Opportunity.hyperFilter({
        [field]: {
          ...(start ? { $gte: start } : {}),
          ...(end ? { $lte: end } : {}),
        },
      });
    };

    const handleClear = () => {
      onSave({
        value: {},
        meta: { startDate: null, endDate: null },
      });
    };

    return (
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
        adapterLocale="he"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginInlineStart: "1rem",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "row", gap: "1rem", flexWrap: "wrap" }}>
            <DateTimePicker
              value={startDate ? dayjs(startDate) : null}
              onChange={(date) => {
                const newStart = date ? dayjs(date).toDate() : null;
                onSave({
                  value: buildFilter(newStart, endDate),
                  meta: { startDate: newStart, endDate },
                });
              }}
              label="מתאריך"
              reduceAnimations
              format="DD/MM/YYYY HH:mm"
              slotProps={{ textField: { size: "small" } }}
            />
            <DateTimePicker
              value={endDate ? dayjs(endDate) : null}
              onChange={(date) => {
                const newEnd = date ? dayjs(date).toDate() : null;
                onSave({
                  value: buildFilter(startDate, newEnd),
                  meta: { startDate, endDate: newEnd },
                });
              }}
              label="עד תאריך"
              reduceAnimations
              format="DD/MM/YYYY HH:mm"
              slotProps={{ textField: { size: "small" } }}
            />
          </div>
          <Button variant="outlined" size="small" onClick={handleClear} sx={{ alignSelf: "flex-start" }}>
            נקה סינון
          </Button>
        </div>
      </LocalizationProvider>
    );
  },
});

const opportunityCreatedAtDateFilter = makeOpportunityDateRangeFilter("opportunityCreatedAt", "תאריך יצירה", "createdAt");
const opportunityUpdatedAtDateFilter = makeOpportunityDateRangeFilter("opportunityUpdatedAt", "תאריך עריכה", "updatedAt");

export const getFilters = (
  currOrganization: Organization,
): HyperTableFilter<Opportunity>[] => {
  const statuses = currOrganization.status || []; // Extract statuses from currOrganization

  const contactPhoneFilter: HyperTableFilter<Opportunity> = {
    id: "contactPhone",
    icon: "string",
    caption: "טלפון פרטי קשר",
    renderFilter: ({ onSave, meta }) => {
      const [value, setValue] = useState<string>(
        getPhoneFilterInputValue(meta.contactPhone as string | undefined),
      );
      const [mode, setMode] = useState<PhoneSearchMode>(
        getPhoneSearchMode(meta.searchMode),
      );

      const applyFilter = (input: string, searchMode: PhoneSearchMode) => {
        const phoneFilter = buildPhoneNumberFieldFilter(input, searchMode);
        onSave({
          value: phoneFilter
            ? Opportunity.hyperFilter({
              contactDetails: {
                $is: { phoneNumber: phoneFilter },
              },
            })
            : {},
          meta: {
            contactPhone: input || undefined,
            searchMode,
          },
        });
      };

      return (
        <PhoneNumberFilterInput
          value={value}
          mode={mode}
          onChange={(input, searchMode) => {
            setValue(input);
            setMode(searchMode);
            applyFilter(input, searchMode);
          }}
          label="טלפון פרטי קשר"
        />
      );
    },
  };

  return [
    { fieldName: "title", id: "title" },
    { fieldName: "location", id: "location" },
    { fieldName: "comments", id: "comments" },
    opportunityAgeRangeFilter,
    opportunityAmountRangeFilter,
    {
      id: "dailyVolunteerTarget",
      icon: "literal",
      caption: "יעד יומי",
      renderFilter: ({ onSave, meta }) => {
        const state = (meta.state as "has" | "reached" | "open" | "") ?? "";
        const options = [
          { value: "has", label: "יש יעד" },
          { value: "reached", label: "הגיע ליעד" },
          { value: "open", label: "לא הגיע" },
        ] as const;
        return (
          <Select
            size="small"
            displayEmpty
            value={state}
            onChange={(event) => {
              const next = event.target.value as "has" | "reached" | "open" | "";
              const label = options.find((option) => option.value === next)?.label ?? "";
              onSave({
                value: next
                  ? Opportunity.dailyVolunteerTargetState({ state: next })
                  : {},
                meta: { state: next, text: label },
              });
            }}
          >
            <MenuItem value="">הכל</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    contactPhoneFilter,
    { fieldName: "isSensitive", id: "isSensitive", caption: "רגישה" },
    {
      id: "volunteerRegistrationFlow",
      icon: "literal",
      caption: "תהליך הרשמה",
      renderFilter: ({ onSave, meta }) => {
        const [selectedFlows, setSelectedFlows] = useState<
          VolunteerRegistrationFlowValue[]
        >(toVolunteerRegistrationFlows((meta as any).selectedFlows));

        const handleChange = (event: any) => {
          const {
            target: { value },
          } = event;
          const selected = typeof value === "string" ? value.split(",") : value;
          setSelectedFlows(toVolunteerRegistrationFlows(selected));
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <Select
              multiple
              value={selectedFlows}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as VolunteerRegistrationFlowValue[]).map((value) => {
                    const opt = VOLUNTEER_REGISTRATION_FLOW_OPTIONS.find(
                      (o) => o.value === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={opt?.label || value}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px" } },
              }}
              fullWidth
            >
              {VOLUNTEER_REGISTRATION_FLOW_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox checked={selectedFlows.includes(opt.value)} />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value: selectedFlows.length
                      ? Opportunity.hyperFilter({
                        volunteerRegistrationFlow: { $in: selectedFlows },
                      })
                      : {},
                    meta: { selectedFlows },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      fieldName: "isLongTerm",
      id: "isLongTerm",
      caption: "התנדבות קבועה",
    },
    {
      id: "volunteerMode",
      icon: "literal",
      caption: "סוג מתנדבים",
      renderFilter: ({ onSave, meta }) => {
        const [selectedModes, setSelectedModes] = useState<
          OpportunityVolunteerModeType[]
        >(toVolunteerModes((meta as any).selectedModes));

        const handleChange = (event: any) => {
          const {
            target: { value },
          } = event;
          const selected = typeof value === "string" ? value.split(",") : value;
          setSelectedModes(toVolunteerModes(selected));
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <Select
              multiple
              value={selectedModes}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as OpportunityVolunteerModeType[]).map((mode) => (
                    <Chip
                      key={mode}
                      label={OPPORTUNITY_VOLUNTEER_MODE_HEB[mode]}
                      size="small"
                    />
                  ))}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px" } },
              }}
              fullWidth
            >
              {VOLUNTEER_MODE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox checked={selectedModes.includes(opt.value)} />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              ))}
            </Select>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value: selectedModes.length
                      ? Opportunity.hyperFilter({
                        volunteerMode: { $in: selectedModes },
                      })
                      : {},
                    meta: { selectedModes },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: "adminRegion",
      icon: "literal",
      caption: "אזור",
      renderFilter: ({ onSave, meta }) => {
        const [jerusalem, setJerusalem] = useState<boolean>(
          (meta.jerusalem as boolean) || false,
        );
        const [coastal, setCoastal] = useState<boolean>(
          (meta.coastal as boolean) || false,
        );

        const handleSave = () => {
          const codes = [
            ...(jerusalem ? OPPORTUNITY_ADMIN_REGION_JERUSALEM_CODES : []),
            ...(coastal ? OPPORTUNITY_ADMIN_REGION_COASTAL_CODES : []),
          ];

          onSave({
            value: codes.length
              ? Opportunity.inCbsDistricts({ codes: [...codes] })
              : {},
            meta: {
              jerusalem,
              coastal,
              text: [
                jerusalem ? "ירושלים" : null,
                coastal ? "מישור החוף" : null,
              ]
                .filter(Boolean)
                .join(", "),
            },
          });
        };

        return (
          <Box
            sx={{
              p: 1.25,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={jerusalem}
                  onChange={(e) => setJerusalem(e.target.checked)}
                />
              }
              label="ירושלים"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={coastal}
                  onChange={(e) => setCoastal(e.target.checked)}
                />
              }
              label="מישור החוף"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{ mt: 1.25 }}
            >
              שמירה
            </Button>
          </Box>
        );
      },
    },
    {
      id: "hasNoCoordinates",
      icon: "boolean",
      caption: "ללא קואורדינטות",
      renderFilter: ({ onSave, meta }) => {
        const [value, setValue] = useState<boolean | null>(
          ([true, false, null, undefined] as unknown[]).includes(meta.value)
            ? ((meta.value ?? null) as boolean | null)
            : null,
        );

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <CustomBooleanFilter
              value={value}
              onChange={(updater) => {
                setValue((prev) =>
                  updater instanceof Function ? updater(prev) : updater,
                );
              }}
              allowUnselect
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value:
                      value === true ? Opportunity.hasNoCoordinates() : {},
                    meta: { value },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: "isRemote",
      icon: "boolean",
      caption: "מרחוק",
      renderFilter: ({ onSave, meta }) => {
        const [value, setValue] = useState<boolean | null>(
          ([true, false, null, undefined] as unknown[]).includes(meta.value)
            ? ((meta.value ?? null) as any)
            : true,
        );

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <CustomBooleanFilter
              value={value}
              onChange={(updater) => {
                setValue((prev) =>
                  updater instanceof Function ? updater(prev) : updater,
                );
              }}
              allowUnselect
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value:
                      value === null
                        ? {}
                        : Opportunity.hyperFilter({ isRemote: value }),
                    meta: { value },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: "populations",
      icon: "literal",
      caption: "אוכלוסיות מקושרות",
      renderFilter: ({ onSave, meta }) => {
        const [selectedPopulationIds, setSelectedPopulationIds] = useState<
          string[]
        >((meta.selectedPopulationIds || []) as string[]);
        const populations = (currOrganization?.populations || [])
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name));

        const handleChange = (event: any) => {
          const {
            target: { value },
          } = event;
          const selectedIds =
            typeof value === "string" ? value.split(",") : value;
          setSelectedPopulationIds(selectedIds);
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <Select
              multiple
              value={selectedPopulationIds}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const population = populations.find((p) => p.id === id);
                    return (
                      <Chip
                        key={id}
                        label={population?.name || id}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px" } },
              }}
              fullWidth
            >
              {populations.map((population) => (
                <MenuItem key={population.id} value={population.id}>
                  <Checkbox
                    checked={selectedPopulationIds.includes(population.id)}
                  />
                  <ListItemText primary={population.name} />
                </MenuItem>
              ))}
            </Select>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value: selectedPopulationIds.length
                      ? Opportunity.hyperFilter({
                        populations: {
                          $some: {
                            populationId: { $in: selectedPopulationIds },
                          },
                        },
                      })
                      : {},
                    meta: { selectedPopulationIds },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: "categories",
      icon: "literal",
      caption: "קטגוריות משויכות",
      renderFilter: ({ onSave, meta }) => {
        const [selectedCategoryIds, setSelectedCategoryIds] = useState<
          string[]
        >((meta.selectedCategoryIds || []) as string[]);
        const categories = (currOrganization?.categories || [])
          .slice()
          .sort(compareBySortOrder);
        const catalogIds = new Set(categories.map((category) => category.id));
        const knownCategoryIds = selectedCategoryIds.filter((id) =>
          catalogIds.has(id),
        );

        const handleChange = (event: any) => {
          const {
            target: { value },
          } = event;
          const selectedIds =
            typeof value === "string" ? value.split(",") : value;
          setSelectedCategoryIds(
            selectedIds.filter((id: string) => catalogIds.has(id)),
          );
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <Select
              multiple
              value={knownCategoryIds}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const name = categories
                      .find((category) => category.id === id)
                      ?.name?.trim();
                    if (!name) return null;
                    return <Chip key={id} label={name} size="small" />;
                  })}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px" } },
              }}
              fullWidth
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Checkbox
                    checked={knownCategoryIds.includes(category.id)}
                  />
                  <ListItemText primary={category.name} />
                </MenuItem>
              ))}
            </Select>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value: knownCategoryIds.length
                      ? Opportunity.hyperFilter({
                        categories: {
                          $some: {
                            categoryId: { $in: knownCategoryIds },
                          },
                        },
                      })
                      : {},
                    meta: { selectedCategoryIds: knownCategoryIds },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: "managerUrgency",
      icon: "literal",
      caption: "דחיפות מנהל",
      renderFilter: ({ onSave, meta }) => {
        const [selectedUrgencies, setSelectedUrgencies] = useState<
          ManagerUrgencyValue[]
        >(toUrgencies(meta.selectedUrgencies));

        const handleChange = (event: any) => {
          const {
            target: { value },
          } = event;
          const selected =
            typeof value === "string" ? value.split(",") : value;
          setSelectedUrgencies(toUrgencies(selected));
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
            }}
          >
            <Select
              multiple
              value={selectedUrgencies}
              onChange={handleChange}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as ManagerUrgencyValue[]).map((value) => {
                    const option = URGENCY_OPTIONS.find(
                      (item) => item.value === value,
                    );
                    return (
                      <Chip
                        key={value}
                        label={option?.label || value}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px" } },
              }}
              fullWidth
            >
              {URGENCY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={selectedUrgencies.includes(option.value)}
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onSave({
                    value: selectedUrgencies.length
                      ? Opportunity.hyperFilter({
                        managerUrgency: { $in: selectedUrgencies },
                      })
                      : {},
                    meta: { selectedUrgencies },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    callCenterLevelFilter(
      "callCenterUrgency",
      "רמת דחיפות",
      "callCenterUrgency",
      CALL_CENTER_URGENCY_OPTIONS,
    ),
    callCenterLevelFilter(
      "callCenterDifficulty",
      "רמת קושי",
      "callCenterDifficulty",
      CALL_CENTER_DIFFICULTY_OPTIONS,
    ),
    { fieldName: "manager.name", caption: "שם מנהל", id: "managerName" },
    {
      fieldName: "associationRequested.user.name",
      caption: "שם מבקש סיוע",
      id: "associationRequestedUsername",
    },
    createPhoneNumberFilter(Opportunity, {
      fieldName: "associationRequested.user.phoneNumber",
      caption: "טלפון מבקש סיוע",
      id: "associationRequestedPhoneNumber",
    }),
    {
      id: "teams",
      icon: "literal",
      caption: "פרויקטים",
      renderFilter: ({ onSave, meta }) => {
        const { teams } = meta;
        return (
          <TeamsSelect
            id="opportunities-table"
            value={teams as string[]}
            onChange={(teams) =>
              onSave({
                value: teams.length
                  ? Opportunity.hyperFilter({
                    teams: {
                      $some: {
                        teamId: teams as string[],
                      },
                    },
                  })
                  : {},
                meta: { teams },
              })
            }
          />
        );
      },
    },
    {
      id: "isAddressInRadiusFromPoint",
      icon: "string",
      caption: "מרחק מכתובת",
      renderFilter: ({ onSave, meta }) => {
        const filterObj = {
          location: meta?.location || "",
          distanceKm: meta?.distanceKm || 0,
          latitude: meta?.latitude || 0,
          longitude: meta?.longitude || 0,
        };

        const [state, setState] = useState(filterObj);

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              paddingInline: "0.5rem",
            }}
          >
            <FormGoogle
              label="מיקום"
              placeValue={state.location as string}
              dir="rtl"
              onLocationChange={(location) => {
                setState({
                  ...state,
                  location: location.locationName,
                  latitude: location.latLng.lat,
                  longitude: location.latLng.lng,
                });
              }}
            />
            <TextField
              type="number"
              label="מרחק בקילומטרים"
              value={state.distanceKm}
              onChange={(e) =>
                setState({
                  ...state,
                  distanceKm: Number(e.target.value) || "",
                })
              }
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <BasicButton
                style={{
                  borderRadius: "10px",
                  backgroundColor: "#05204c",
                  padding: "0.3rem 1rem",
                  fontWeight: 400,
                  fontSize: "small",
                }}
                text="שמירה"
                onClick={() => {
                  onSave({
                    value: Opportunity.isAddressInRadiusFromPoint({
                      radius: {
                        distance: Number(state.distanceKm),
                        measure: "km",
                      },
                      lat: state.latitude as number,
                      lng: state.longitude as number,
                    }),
                    meta: state,
                  });
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: "isFull",
      icon: "boolean",
      caption: "האם מלאה?",
      renderFilter: ({ onSave, meta }) => {
        const { isFull } = meta;
        return (
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFull === true}
                    onChange={() =>
                      onSave({
                        value: Opportunity.isFull(),
                        meta: { isFull: true },
                      })
                    }
                  />
                }
                label="כן"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFull === false}
                    onChange={() =>
                      onSave({
                        value: Opportunity.isInNeedOfParticipants({}),
                        meta: { isFull: false },
                      })
                    }
                  />
                }
                label="לא"
              />
            </div>
          </div>
        );
      },
    },
    {
      id: "hasInterested",
      icon: "boolean",
      caption: "יש מתעניינים",
      renderFilter: ({ onSave, meta }) => {
        const hasInterested = meta.hasInterested as boolean | undefined;
        return (
          <Box
            sx={{
              p: "10px",
              display: "flex",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasInterested === true}
                  onChange={() =>
                    onSave({
                      value: Opportunity.hyperFilter({
                        participantUsers: {
                          $some: { status: "PENDING" },
                        },
                      }),
                      meta: { hasInterested: true, text: "יש מתעניינים" },
                    })
                  }
                />
              }
              label="יש מתעניינים"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasInterested === false}
                  onChange={() =>
                    onSave({
                      value: Opportunity.hyperFilter({
                        participantUsers: {
                          $none: { status: "PENDING" },
                        },
                      }),
                      meta: { hasInterested: false, text: "אין מתעניינים" },
                    })
                  }
                />
              }
              label="אין מתעניינים"
            />
          </Box>
        );
      },
    },
    {
      id: "requiredGender",
      icon: "literal",
      caption: "מגדר נדרש",
      renderFilter: ({ onSave, meta }) => {
        const value = (meta.value as string) ?? "";
        return (
          <Box sx={{ p: 1, minWidth: 200 }}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={value}
              onChange={(event) => {
                const next = event.target.value;
                const filter =
                  next === "NONE"
                    ? Opportunity.hyperFilter({
                        $or: [{ requiredGender: null }, { requiredGender: "" }],
                      })
                    : next
                      ? Opportunity.hyperFilter({
                          requiredGender: next as "MALE" | "FEMALE" | "OTHER",
                        })
                      : {};
                onSave({
                  value: filter,
                  meta: {
                    value: next,
                    text:
                      next === "NONE"
                        ? "בלי דרישה"
                        : next === "MALE"
                          ? "זכר"
                          : next === "FEMALE"
                            ? "נקבה"
                            : next === "OTHER"
                              ? "אחר"
                              : "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="MALE">זכר</MenuItem>
              <MenuItem value="FEMALE">נקבה</MenuItem>
              <MenuItem value="OTHER">אחר</MenuItem>
              <MenuItem value="NONE">בלי דרישה</MenuItem>
            </Select>
          </Box>
        );
      },
    },
    {
      id: "requiresVehicle",
      icon: "boolean",
      caption: "דורש רכב",
      renderFilter: ({ onSave, meta }) => {
        const requiresVehicle = meta.requiresVehicle as boolean | undefined;
        return (
          <Box sx={{ p: "10px", display: "flex", flexDirection: "row", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresVehicle === true}
                  onChange={() =>
                    onSave({
                      value: Opportunity.hyperFilter({ requiresVehicle: true }),
                      meta: { requiresVehicle: true, text: "דורש רכב" },
                    })
                  }
                />
              }
              label="כן"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresVehicle === false}
                  onChange={() =>
                    onSave({
                      value: Opportunity.hyperFilter({ requiresVehicle: false }),
                      meta: { requiresVehicle: false, text: "לא דורש רכב" },
                    })
                  }
                />
              }
              label="לא"
            />
          </Box>
        );
      },
    },
    { fieldName: "startDate", id: "startDate" },
    { fieldName: "endDate", id: "endDate" },
    {
      id: "status",
      icon: "literal",
      caption: "סטטוס",
      renderFilter: ({ onSave, meta }) => {
        const [metaStatuses, setMetaStatuses] = useState<string[]>(
          (meta.statuses as string[]) || [],
        ); // State for selected statuses
        const [noStatus, setNoStatus] = useState<boolean>(
          (meta.noStatus as boolean) || false,
        ); // State for "no status" checkbox

        const handleSave = () => {
          const conditions = [];
          if (metaStatuses.length) {
            conditions.push({
              progressStatus: {
                $is: {
                  id: {
                    $in: metaStatuses.map(
                      (id) =>
                        statuses.find((status) => status.id === id)?.id,
                    ),
                  },
                },
              },
            });
          }
          if (noStatus) {
            conditions.push({ progressStatus: { $is: null } });
          }

          onSave({
            value: conditions.length
              ? Opportunity.hyperFilter({ $or: conditions }) // Combine conditions with $or
              : {}, // Clear filter if no conditions
            meta: { statuses: metaStatuses, noStatus },
          });
        };

        return (
          <div
            style={{
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              overflowY: "auto",
              maxHeight: "300px", // Limit height for better UX
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={noStatus} // Check if noStatus is selected
                  onChange={(e) => setNoStatus(e.target.checked)} // Update state
                />
              }
              label="ללא סטטוס" // Label for opportunities without a status
            />
            {statuses
              ?.filter((status) => status.type === "MISSION") // Filter by type "MISSION"
              .map((status) => (
                <FormControlLabel
                  key={status.id}
                  control={
                    <Checkbox
                      checked={metaStatuses.includes(status.id)} // Check if ID is in metaStatuses
                      onChange={(e) => {
                        setMetaStatuses(
                          (prev) =>
                            e.target.checked
                              ? [...prev, status.id] // Add ID if checked
                              : prev.filter((id) => id !== status.id), // Remove ID if unchecked
                        );
                      }}
                    />
                  }
                  label={status.title}
                />
              ))}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              style={{ marginTop: "10px" }}
            >
              שמירה
            </Button>
          </div>
        );
      },
    },
    {
      id: "partnerOrganizationId",
      icon: "literal",
      caption: "ארגון שותף",
      renderFilter: ({ onSave, meta }) => {
        const [selectedOrgs, setSelectedOrgs] = useState<PartnerOrganization[]>(
          (meta.selectedOrgs as PartnerOrganization[]) ?? [],
        );
        const [allOrgs, setAllOrgs] = useState<PartnerOrganization[]>([]);
        const [orgSearch, setOrgSearch] = useState("");
        const [loading, setLoading] = useState(true);

        useEffect(() => {
          let cancelled = false;
          const handle = window.setTimeout(() => {
            void (async () => {
              setLoading(true);
              try {
                const q = orgSearch.trim();
                const list = await partnerOrganizationRepo.find({
                  ...(q ? { where: { name: { $contains: q } } } : {}),
                  orderBy: { name: "asc" },
                  limit: q ? 30 : 200,
                });
                if (!cancelled) setAllOrgs(list);
              } finally {
                if (!cancelled) setLoading(false);
              }
            })();
          }, 250);
          return () => {
            cancelled = true;
            window.clearTimeout(handle);
          };
        }, [orgSearch]);

        const orgOptions = [...selectedOrgs, ...allOrgs].filter(
          (org, index, list) => list.findIndex((o) => o.id === org.id) === index,
        );

        const handleChange = (event: any) => {
          const {
            target: { value },
          } = event;
          const selectedIds =
            typeof value === "string" ? value.split(",") : value;
          setSelectedOrgs(
            orgOptions.filter((org) => selectedIds.includes(org.id)),
          );
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
              minWidth: 280,
            }}
          >
            <TextField
              size="small"
              label="חיפוש ארגון"
              value={orgSearch}
              onChange={(event) => setOrgSearch(event.target.value)}
              fullWidth
            />
            <Select
              multiple
              value={selectedOrgs.map((o) => o.id)}
              onChange={handleChange}
              disabled={loading}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const org = orgOptions.find((o) => o.id === id);
                    return (
                      <Chip
                        key={id}
                        label={org?.name || id}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: { sx: { borderRadius: "20px", maxHeight: 320 } },
              }}
              fullWidth
            >
              {orgOptions.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  <Checkbox checked={selectedOrgs.some((o) => o.id === org.id)} />
                  <ListItemText primary={org.name} />
                </MenuItem>
              ))}
            </Select>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const ids = selectedOrgs.map((o) => o.id);
                  onSave({
                    value: ids.length
                      ? opportunitiesWithPartnerOrganizationWhere(ids)
                      : {},
                    meta: {
                      selectedOrgs,
                      text: selectedOrgs.map((o) => o.name).join(", "),
                    },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: "missingAssociations",
      icon: "literal",
      caption: "חסר שיוך",
      renderFilter: ({ onSave, meta }) => {
        const [withoutPartnerOrganization, setWithoutPartnerOrganization] =
          useState<boolean>(
            (meta.withoutPartnerOrganization as boolean) || false,
          );
        const [withoutCategories, setWithoutCategories] = useState<boolean>(
          (meta.withoutCategories as boolean) || false,
        );

        const handleSave = () => {
          const conditions: Array<Record<string, unknown>> = [];
          if (withoutPartnerOrganization) {
            conditions.push(opportunitiesWithoutPartnerOrganizationFilter());
          }
          if (withoutCategories) {
            conditions.push({ categories: { $none: {} } });
          }

          const value =
            conditions.length === 0
              ? {}
              : conditions.length === 1
                ? Opportunity.hyperFilter(conditions[0]!)
                : Opportunity.hyperFilter({ $or: conditions });

          onSave({
            value,
            meta: {
              withoutPartnerOrganization,
              withoutCategories,
              text: [
                withoutPartnerOrganization ? "ללא ארגון שותף" : null,
                withoutCategories ? "ללא קטגוריות" : null,
              ]
                .filter(Boolean)
                .join(", "),
            },
          });
        };

        return (
          <Box
            sx={{
              p: 1.25,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={withoutPartnerOrganization}
                  onChange={(e) =>
                    setWithoutPartnerOrganization(e.target.checked)
                  }
                />
              }
              label="ללא ארגון שותף"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={withoutCategories}
                  onChange={(e) => setWithoutCategories(e.target.checked)}
                />
              }
              label="ללא קטגוריות"
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{ mt: 1.25 }}
            >
              שמירה
            </Button>
          </Box>
        );
      },
    },
    {
      id: "partnerOrganizationManager",
      icon: "literal",
      caption: "מנהל ארגוני של ארגון שותף",
      renderFilter: ({ onSave, meta }) => {
        const [selectedManagers, setSelectedManagers] = useState<
          OrganizationManager[]
        >((meta.selectedManagers as OrganizationManager[]) ?? []);
        const [options, setOptions] = useState<OrganizationManager[]>([]);
        const [search, setSearch] = useState("");

        const searchManagers = useCallback(async (query: string) => {
          const q = query.trim();
          if (q.length < 2) {
            setOptions([]);
            return;
          }
          const phoneQuery = normalizePhoneDigitsForSearch(q);
          const list = await organizationManagerRepo.find({
            where: OrganizationManager.hyperFilter({
              $or: [
                { name: { $contains: q } },
                { phoneNumber: { $contains: phoneQuery } },
              ],
            }),
            include: { organization: true },
            orderBy: { name: "asc" },
            limit: 25,
          });
          setOptions(list);
        }, []);

        useEffect(() => {
          const handle = window.setTimeout(() => {
            void searchManagers(search);
          }, 250);
          return () => window.clearTimeout(handle);
        }, [search, searchManagers]);

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              paddingInline: "0.5rem",
              minWidth: 280,
            }}
          >
            <Autocomplete
              multiple
              options={options}
              value={selectedManagers}
              onChange={(_, value) => setSelectedManagers(value)}
              inputValue={search}
              onInputChange={(_, v) => setSearch(v)}
              getOptionLabel={(m) =>
                `${m.name}${m.organization?.name ? ` · ${m.organization.name}` : ""}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="הקלד לפחות 2 תווים"
              renderTags={(value, getTagProps) =>
                value.map((m, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={m.id}
                    label={m.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="חיפוש מנהל ארגוני"
                  size="small"
                />
              )}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const managerIds = selectedManagers.map((m) => m.id);
                  onSave({
                    value: managerIds.length
                      ? Opportunity.hyperFilter({
                          partnerOrganization: {
                            $is: {
                              organizationManagers: {
                                $some: {
                                  managerId: { $in: managerIds },
                                },
                              },
                            },
                          },
                        })
                      : {},
                    meta: {
                      selectedManagers,
                      text: selectedManagers.map((m) => m.name).join(", "),
                    },
                  });
                }}
              >
                שמירה
              </Button>
            </div>
          </div>
        );
      },
    },
    opportunityCreatedAtDateFilter,
    opportunityUpdatedAtDateFilter,
  ];
};

export default getFilters;
